import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth, useAuthenticatedFetch } from './AuthContext';
import { usePlan } from './PlanContext';
import { useClients } from './ClientContext';
import type { Asset, AssetFolder } from '../types';
import { STORAGE_LIMITS } from '../types';

interface AssetContextValue {
  assets: Asset[];
  folders: AssetFolder[];
  currentFolderId: string | null;
  isLoading: boolean;
  error: string | null;
  storageUsed: number;
  storageLimit: number;
  storagePercentage: number;
  // Navigation
  navigateToFolder: (folderId: string | null) => void;
  getBreadcrumbs: () => AssetFolder[];
  // Asset operations
  uploadAsset: (file: File, folderId?: string) => Promise<Asset | null>;
  deleteAsset: (id: string) => Promise<boolean>;
  renameAsset: (id: string, name: string) => Promise<boolean>;
  moveAsset: (id: string, folderId: string | null) => Promise<boolean>;
  // Folder operations
  createFolder: (name: string, parentId?: string) => Promise<AssetFolder | null>;
  deleteFolder: (id: string) => Promise<boolean>;
  renameFolder: (id: string, name: string) => Promise<boolean>;
  // Refresh
  refreshAssets: () => Promise<void>;
}

const AssetContext = createContext<AssetContextValue | undefined>(undefined);

// Helper to convert API asset to frontend Asset
function parseAsset(apiAsset: Record<string, unknown>): Asset {
  return {
    id: apiAsset.id as string,
    userId: apiAsset.user_id as string,
    name: apiAsset.name as string,
    type: apiAsset.type as 'image' | 'video',
    mimeType: apiAsset.mime_type as string,
    size: apiAsset.size as number,
    url: apiAsset.url as string,
    thumbnailUrl: apiAsset.thumbnail_url as string | undefined,
    objectKey: apiAsset.object_key as string,
    folderId: apiAsset.folder_id as string | undefined,
    clientId: apiAsset.client_id as string | undefined,
    createdAt: new Date(apiAsset.created_at as string),
    updatedAt: new Date(apiAsset.updated_at as string),
  };
}

// Helper to convert API folder to frontend AssetFolder
function parseFolder(apiFolder: Record<string, unknown>): AssetFolder {
  return {
    id: apiFolder.id as string,
    userId: apiFolder.user_id as string,
    name: apiFolder.name as string,
    parentId: apiFolder.parent_id as string | undefined,
    clientId: apiFolder.client_id as string | undefined,
    createdAt: new Date(apiFolder.created_at as string),
    updatedAt: new Date(apiFolder.updated_at as string),
  };
}

export function AssetProvider({ children }: { children: ReactNode }) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [folders, setFolders] = useState<AssetFolder[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated, token } = useAuth();
  const { subscription } = usePlan();
  const { activeClientId } = useClients();
  const authFetch = useAuthenticatedFetch();

  // Calculate storage
  const storageUsed = assets.reduce((sum, asset) => sum + asset.size, 0);
  const storageLimit = STORAGE_LIMITS[subscription.planId];
  const storagePercentage = storageLimit > 0 ? (storageUsed / storageLimit) * 100 : 0;

  // Load assets and folders (filtered by active client)
  const refreshAssets = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setAssets([]);
      setFolders([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build query params for client filtering
      const params = new URLSearchParams();
      if (activeClientId === 'personal') {
        params.set('clientId', 'personal');
      } else if (activeClientId) {
        params.set('clientId', activeClientId);
      }
      // If activeClientId is null, fetch all assets (no filter)

      const queryString = params.toString();
      const assetsUrl = queryString ? `/assets?${queryString}` : '/assets';
      const foldersUrl = queryString ? `/assets/folders?${queryString}` : '/assets/folders';

      const [assetsRes, foldersRes] = await Promise.all([
        authFetch(assetsUrl).catch(() => null),
        authFetch(foldersUrl).catch(() => null),
      ]);

      if (assetsRes?.ok) {
        const data = await assetsRes.json();
        setAssets((data.assets || []).map(parseAsset));
      } else {
        // API not available yet - silently set empty
        setAssets([]);
      }

      if (foldersRes?.ok) {
        const data = await foldersRes.json();
        setFolders((data.folders || []).map(parseFolder));
      } else {
        setFolders([]);
      }
    } catch (err) {
      console.error('Failed to load assets:', err);
      // Don't show error for missing API - it's expected for now
      setAssets([]);
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token, authFetch, activeClientId]);

  useEffect(() => {
    refreshAssets();
  }, [refreshAssets]);

  // Navigation
  const navigateToFolder = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId);
  }, []);

  const getBreadcrumbs = useCallback((): AssetFolder[] => {
    if (!currentFolderId) return [];

    const breadcrumbs: AssetFolder[] = [];
    let folder = folders.find(f => f.id === currentFolderId);

    while (folder) {
      breadcrumbs.unshift(folder);
      folder = folder.parentId ? folders.find(f => f.id === folder!.parentId) : undefined;
    }

    return breadcrumbs;
  }, [currentFolderId, folders]);

  // Asset operations
  const uploadAsset = useCallback(async (file: File, folderId?: string): Promise<Asset | null> => {
    // Check storage limit
    if (storageUsed + file.size > storageLimit) {
      setError('Storage limit exceeded. Please delete some files or upgrade your plan.');
      return null;
    }

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Determine clientId to assign
      let clientId: string | undefined;
      if (activeClientId && activeClientId !== 'personal') {
        clientId = activeClientId;
      }

      const response = await authFetch('/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: file.name,
          mimeType: file.type,
          size: file.size,
          data: base64,
          folderId: folderId || currentFolderId || undefined,
          clientId,
        }),
      }).catch(() => null);

      if (!response) {
        setError('Drive storage is coming soon. Stay tuned!');
        return null;
      }

      if (response.ok) {
        const data = await response.json();
        const newAsset = parseAsset(data.asset);
        setAssets(prev => [newAsset, ...prev]);
        return newAsset;
      }

      const errData = await response.json();
      setError(errData.error || 'Failed to upload asset');
      return null;
    } catch (err) {
      console.error('Failed to upload asset:', err);
      setError('Network error uploading asset');
      return null;
    }
  }, [authFetch, currentFolderId, storageUsed, storageLimit, activeClientId]);

  const deleteAsset = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/assets/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setAssets(prev => prev.filter(a => a.id !== id));
        return true;
      }

      console.error('Failed to delete asset:', await response.text());
      return false;
    } catch (err) {
      console.error('Failed to delete asset:', err);
      return false;
    }
  }, [authFetch]);

  const renameAsset = useCallback(async (id: string, name: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/assets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAsset = parseAsset(data.asset);
        setAssets(prev => prev.map(a => a.id === id ? updatedAsset : a));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to rename asset:', err);
      return false;
    }
  }, [authFetch]);

  const moveAsset = useCallback(async (id: string, folderId: string | null): Promise<boolean> => {
    try {
      const response = await authFetch(`/assets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId }),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAsset = parseAsset(data.asset);
        setAssets(prev => prev.map(a => a.id === id ? updatedAsset : a));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to move asset:', err);
      return false;
    }
  }, [authFetch]);

  // Folder operations
  const createFolder = useCallback(async (name: string, parentId?: string): Promise<AssetFolder | null> => {
    try {
      // Determine clientId to assign
      let clientId: string | undefined;
      if (activeClientId && activeClientId !== 'personal') {
        clientId = activeClientId;
      }

      const response = await authFetch('/assets/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          parentId: parentId || currentFolderId || undefined,
          clientId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const newFolder = parseFolder(data.folder);
        setFolders(prev => [newFolder, ...prev]);
        return newFolder;
      }

      const errData = await response.json();
      setError(errData.error || 'Failed to create folder');
      return null;
    } catch (err) {
      console.error('Failed to create folder:', err);
      return null;
    }
  }, [authFetch, currentFolderId, activeClientId]);

  const deleteFolder = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/assets/folders/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setFolders(prev => prev.filter(f => f.id !== id));
        // Also remove assets that were in this folder from local state
        setAssets(prev => prev.filter(a => a.folderId !== id));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to delete folder:', err);
      return false;
    }
  }, [authFetch]);

  const renameFolder = useCallback(async (id: string, name: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/assets/folders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedFolder = parseFolder(data.folder);
        setFolders(prev => prev.map(f => f.id === id ? updatedFolder : f));
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to rename folder:', err);
      return false;
    }
  }, [authFetch]);

  return (
    <AssetContext.Provider
      value={{
        assets,
        folders,
        currentFolderId,
        isLoading,
        error,
        storageUsed,
        storageLimit,
        storagePercentage,
        navigateToFolder,
        getBreadcrumbs,
        uploadAsset,
        deleteAsset,
        renameAsset,
        moveAsset,
        createFolder,
        deleteFolder,
        renameFolder,
        refreshAssets,
      }}
    >
      {children}
    </AssetContext.Provider>
  );
}

export function useAssets() {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error('useAssets must be used within an AssetProvider');
  }
  return context;
}
